import Radio from "backbone.radio";
import { View } from "extensions/marionette";
import $ from "jquery";
import { _ } from "extensions/underscore";

import strings from "common/strings";

import { Loader } from "common/components/loader";
import { Dialog } from "common/components/dialog/dialog";
import { Qualifiers } from "common/components/queryBuilder/qualifiers";

import { SettingsView } from "../widgetManager/widgetSettings";

import { getTemplate } from "./summaryTemplates/summaryTemplates";

import { mergeFilters } from "./widgetHelper";

export const DefaultView = View.extend({
    type: "default",
    isResizing: false,
    updateNote: true,
    className() {
        return `grid-stack-item widget3 ${this.type} ${this.model.get("widgetSubtype")}`;
    },
    attributes() {
        return {
            "gs-x": this.model.get("x"),
            "gs-y": this.model.get("y"),
            "gs-w": this.model.get("w"),
            "gs-h": this.model.get("h"),
            "gs-auto-position": this.model.get("auto") ? "yes" : null,
            "gs-max-h": 2,
            "gs-min-h": 2,
            "gs-min-w": 2,
            "gs-max-w": 12,
            "data-cid": this.cid
        };
    },
    
    ui: {
        content: ".grid-stack-item-content",
        widgetEdit: ".widgetEdit"
    },
    
    childViewEvents: {
        drilldown: "drilldownToCorrespondingPage"
    },
    
    // `click` is a `mousedown` followed by a `mouseup`
    // Checking if the mouse moved between `mousedown` and `mouseup` and setting a flag if intent was to `click`
    events: {
        mousedown() {
            this.isClicking = true;
        },
        mousemove() {
            this.isClicking = false;
        },
        "click .retry-widget": "refetchWidgetData",
        "click .widgetEdit": "editWidgetSettings"
    },
    templateContext() {
        const filterNotification = this.getIndicatorInfo();
        return {
            indicator: filterNotification.cname,
            tt: filterNotification.tooltip
        };
    },
    editWidgetSettings(e) {
        e.preventDefault();
        e.stopPropagation();
        Dialog.show({
            title: this.model.get("widgetSubtype").toUpperCase(),
            dismissable: true,
            backdrop: "static",
            content: new SettingsView({
                srcWidget: this,
                model: this.model
            }),
            render: true
        });
    },
    
    refreshWidgetOnEdit(force = false) {
        if (force) {
            this.setDataCollection();
            this.refetchWidgetData({
                preventDefault: () => {},
                stopPropagation: () => {}
            });
            const filterNotification = this.getIndicatorInfo();
            this.ui.widgetEdit.removeClass("on conflict").addClass(filterNotification.cname);
            this.ui.widgetEdit[0].setAttribute("title", filterNotification.tooltip);
            this.ui.widgetEdit[0].dataset.originalTitle = filterNotification.tooltip;
        } else {
            this.reflowWidget();
        }
    },
    
    drilldownToCorrespondingPage(actor, x) {
        // Don't drilldown if widget is moving, resizeing, or event is triggered when selecting text
        if (this.isClicking === false || this.isResizing || this.isDragging) {
            return;
        }
        
        // Don't drill down on re-fetch link
        // Don't drilldown on inspector icon clicks
        if (actor && actor.target && ($(actor.target).hasClass("retry-widget") || $(actor.target).hasClass("inspector-target"))) {
            return;
        }
        
        this._doDrilldown.apply(this, arguments);
    },
    
    _doDrilldown(actor, x) {
        this.model.subtypeModel.get("drilldown")(this.model.get("widgetType"), actor, x);
    },
    
    refetchWidgetData(e) {
        e.preventDefault();
        e.stopPropagation();
        this.listenTo(this.collection, "fetched", () => {
            this.handleSuccess();
        });
        this.collection.fetch();
    },
    
    getRequestParams() {
        return {};
    },
    
    getCollectionSortMode() {
        return "client";
    },
    
    getIndicatorInfo() {
        let indicatorType = "none";
        const indicator = {
            none: {
                cname: "",
                tooltip: ""
            },
            on: {
                cname: "on",
                tooltip: strings.dashboard.filters.on
            },
            conflict: {
                cname: "conflict",
                tooltip: strings.dashboard.filters.conflict
            }
        };
        if (this.filterInfo?.widgetHasFilter) {
            if (this.filterInfo.widgetHasConflict) {
                indicatorType = "conflict";
            } else {
                indicatorType = "on";
            }
        }
        return indicator[indicatorType];
    },
    
    mergeFilters() {
        const widgetQualifiers = new Qualifiers({ value: this.model.get("qualifiers") });
        const widgetCollectionParams = this.getOption("parent").getViewCollection({
            url: _.result(this, "getAPIEndpoint"),
            params: this.getRequestParams(),
            mode: this.getCollectionSortMode()
        });
        const dashboardQualifiers = new Qualifiers({ value: widgetCollectionParams.collection.queryParams.q });
        this.filterInfo = mergeFilters(dashboardQualifiers, widgetQualifiers);
        return this.filterInfo.mergedString;
    },
    
    setDataCollection() {
        const widgetCollectionParams = this.getOption("parent").getViewCollection({
            url: _.result(this, "getAPIEndpoint"),
            params: this.getRequestParams(),
            mode: this.getCollectionSortMode()
        });
        
        this.collection = widgetCollectionParams.collection;
        this.collection.queryParams.q = this.mergeFilters();
        this.skipReactOnChanges = !widgetCollectionParams.isNew;
        
        this.listenTo(this.collection, "before:fetched", () => {
            this.triggerMethod("loading", null, this);
            this.addSpinners();
        });
        this.listenTo(this.collection, "fetched", this.handleSuccess);
        this.listenTo(this.collection, "fetchError", this.handleError);
    },
    
    handleSuccess() {
        this.removeSpinners();
        this.hasAPIError = false;
        this.triggerMethod("loaded", null, this);
    },
    
    handleError() {
        // Remove spinners
        this.removeSpinners();
        this.hasAPIError = true;
        
        if (_.isFunction(this.showErrorMessage)) {
            this.showErrorMessage();
        }
        this.triggerMethod("loaded", true, this);
    },
    
    getAPIEndpoint() {
        const endpoint = this.model.subtypeModel.get("api_endpoint");
        const pivot = this.model.getOption("pivot");
        
        return _.isFunction(endpoint) ? endpoint(pivot) : endpoint;
    },
    
    /**
     * Defined by subclasses - which children to hide during loading procedure
     * @returns {*}
     */
    getLoadingChildren() {
        return this.ui.content.children();
    },
    
    addSpinners() {
        if (!this.isRendered()) {
            return;
        }
        const content = this.ui.content;
        const children = this.getLoadingChildren();
        
        children.fadeOut(0, () => {
            Loader.addTo(content);
        });
    },
    
    removeSpinners() {
        const content = this.ui.content;
        const children = this.getLoadingChildren();
        Loader.removeFrom(content);
        children.fadeIn(300);
    },
    
    makeWidgetTitle() {
        const pivot = this.model.get("options").pivot;
        const title = this.model.get("title");
        if (pivot) {
            return `${title} by ${Radio.channel("main").request("get:getters").findPivotName(pivot).toLowerCase()}`;
        }
        
        return title;
    },
    
    addWidgetTitle() {
        const title = this.makeWidgetTitle();
        this.ui.widgetTitle.text(title);
    },
    
    getValuesForTemplate(templateData, totals) {
        return _.mapObject(templateData, (val, key) => {
            // Get value from the template data
            
            const value = _.isFunction(val.value) ? val.value(totals) : totals[val.value];
            let formattedValue = value;
            if (val.type === "number") {
                formattedValue = this.formatNumber(value);
            } else if (val.type === "amount") {
                formattedValue = this.formatAmountWithCurrentCurrency(value);
            }
            
            return formattedValue;
        });
    },
    
    /**
     * Update values for quick widget
     */
    updateValues() {
        if (this.model.get("options").showSummary === false) {
            this.$(".widgetData").remove();
            return;
        }
        
        const subtypeModel = this.model.subtypeModel;
        const totals = this.collection.totals(subtypeModel.getClone("totals"));
        const data = subtypeModel.getClone("quick").data;
        
        let summaryTemplate;
        if (data.widgetValueTemplate) {
            summaryTemplate = data.widgetValueTemplate;
        } else {
            summaryTemplate = getTemplate(data.summaryTemplate);
        }
        
        const templateData = data.widgetValueTemplateData;
        
        const resolvedData = this.getValuesForTemplate(templateData, totals);
        // eslint-disable-next-line guard-for-in,no-restricted-syntax
        for (const prop in templateData) {
            const item = templateData[prop];
            if (item.activeValue && item.headerTemplateKey) {
                resolvedData[item.headerTemplateKey] = resolvedData[prop];
                resolvedData[`${item.headerTemplateKey}_label`] = templateData[prop].label;
            }
        }
        
        this.ui.widgetValue.html(summaryTemplate(resolvedData)).addClass(data.summaryTemplate);
        
        if (this.getOption("updateNote")) {
            this.ui.widgetNote.html(data.widgetNote);
        }
    },
    
    reactOnGlobalChanges() {
        if (this.skipReactOnChanges) {
            return;
        }
        
        this.setupCurrencyPicker({
            structure: this.collection
        });
        this.setupDatePicker({
            structure: this.collection
        });
        
        this.rerenderOnEntityChange(this.collection);
    },
    
    getChart() {
        const chartRegion = this.getRegion("chart");
        if (chartRegion && chartRegion.hasView()) {
            return chartRegion.currentView;
        }
    },
    
    onRender() {
        // If it's not a custom model without an api_endpoint than do not display a widget. It's malformed
        if (!this.model.isCustom && !this.model.subtypeModel.get("api_endpoint")) {
            return this.destroy();
        }
        this.reactOnGlobalChanges();
        $(window).on(`resize.dashboardwidget.${this.cid}`, _.debounce(this.reflowWidget.bind(this), 300));
    },
    
    onDestroy() {
        this.grid = null;
        $(window).off(`resize.dashboardwidget.${this.cid}`);
    },
    
    reflowWidget() {
        // Return earlier if the view already destroyed
        if (this.isDestroyed()) { return; }
        
        this.onGridstackInit();
        
        const chart = this.getChart();
        if (chart && _.isFunction(chart.reflow)) {
            chart.reflow();
        }
    }
}, {
    baseScreenWidth: 1440,
    getScaleCoefficient() {
        return document.body.clientWidth / 1440;
    }
});
